import { useHistory } from 'react-router-dom';
import userHaveRolePermission from '../../../../helpers/userHaveRolePermission';
import { PAGE_URLS, PARENT_ROUTES } from '../../../../helpers/endpoints';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Col,
  ConfigProvider,
  Form,
  Modal,
  notification,
  Row,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import React, { Suspense, useCallback, useEffect, useState } from 'react';
import {
  CSV_ROUTING_EXPORT_COLUMNS,
  debouncedDataLoadWaitMs,
  ORDER_TYPES,
  SOLVER_STATUS,
  STATUS,
} from '../../../../helpers/constants';
import debounce from 'lodash/debounce';
import {
  DeleteOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
  PlusOutlined,
  RedoOutlined,
  SendOutlined,
} from '@ant-design/icons';
import trTR from 'antd/es/locale/tr_TR';
import moment from 'moment';
import {
  getRoutingViewList,
  removeRoutingViewListData,
  setSelectedRoutingDetailData,
} from '../../../../store/requests/routing';
import { useSelector } from 'react-redux';

const RoutingViewListFilter = React.lazy(
  () => import('./RoutingViewListFilter'),
);

let lastQuery = false;
const { confirm } = Modal;

const RoutingViewList = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const [exportButtonLoading, setExportButtonLoading] = useState(false);
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [routeTaskIdList, setRouteTaskIdList] = useState([]);
  const [defaultDateSort, setDefaultDateSort] = useState(true);
  const activeStoreID = useSelector(
    (state) => state.auth?.user?.activeStore?.id,
  );
  const [tableParams, setTableParams] = useState({
    pagination: {
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total) => `Toplam ${total} kayıt`,
      pageSizeOptions: [25, 50, 100],
      size: 'small',
      current: 1,
      pageSize: 25,
    },
  });

  useEffect(() => {
    fetchData(false);
    setIsFiltersOpen(false);
    return () => {
      lastQuery = null;
      setLoading(false);
    };
  }, [JSON.stringify(tableParams), JSON.stringify(filters)]);

  const fetchData = (initialSort) => {
    const queryString = getQueryString(initialSort);
    loadData(queryString);
  };
  const removeItemFromFilters = (destinationItem) => {
    Object.keys(filters).forEach((item) => {
      if (item === destinationItem) {
        const editedFilters = {
          ...filters,
          [destinationItem]: undefined,
        };
        setFilters(editedFilters);
        form.setFieldsValue(editedFilters);
      }
    });
    setDefaultDateSort(true);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      ...sorter,
    });
  };

  const getQueryString = (initialSort = false) => {
    const filterParts = [];
    let queryString = 'filter=active==true';

    if (activeStoreID) {
      queryString = `filter=active==true and courierTask.slot.zone.store.id==${activeStoreID}`;
    }

    if (Object.values(filters).some((item) => !!item)) {
      Object.keys(filters).forEach((item) => {
        if (filters[item]) {
          let filterPart = '';
          const filterMappings = {
            zone: 'courierTask.slot.zone.id=in',
            store: 'courierTask.slot.zone.store.id=in',
            startTime: 'courierTask.slot.startTime=in',
            endTime: 'courierTask.slot.endTime',
            status: 'status=in',
            slotStartDate: 'courierTask.slot.slotDate.date',
            slotEndDate: 'courierTask.slot.slotDate.date',
          };

          filterPart = filterMappings[item] || item;
          filterPart +=
            (item === 'taskStartTime'
              ? '>='
              : item === 'taskEndTime'
                ? '<='
                : item === 'slotStartDate'
                  ? '>='
                  : item === 'slotEndDate'
                    ? '<='
                    : item == 'startTime' ||
                        item == 'store' ||
                        item == 'zone' ||
                        item == 'status'
                      ? '='
                      : '==') + filters[item];

          filterParts.push(filterPart);
        }
      });

      const fullFilter = filterParts.join(' and ');
      queryString = `filter=active==true and ${fullFilter}`;
    }

    if (defaultDateSort || initialSort) {
      queryString = `${queryString}&sort=taskStartTime,desc`;
      setDefaultDateSort(false);
    }

    queryString += `&page=${tableParams.pagination.current - 1}&size=${tableParams.pagination.pageSize}`;

    if (tableParams.column) {
      queryString += `&sort=${tableParams.column.dataIndex},${ORDER_TYPES[tableParams.order]}`;
    }

    return queryString;
  };

  async function loadDataWithDebounce(queryString) {
    if (!loading) {
      lastQuery = queryString;
      setLoading(true);
      getRoutingViewList(queryString)
        .then((response) => {
          const { _embedded, page } = response;
          const { routeTaskResponseDtoList } = _embedded ?? [];
          setRows(routeTaskResponseDtoList ?? []);
          setTableParams({
            ...tableParams,
            pagination: {
              ...tableParams.pagination,
              total: page.totalElements,
            },
          });
        })
        .finally(() => setLoading(false));
    }
  }

  const loadData = useCallback(
    debounce((queryString) => {
      loadDataWithDebounce(queryString);
    }, debouncedDataLoadWaitMs),
  );

  const columns = [
    {
      title: t('routeId'),
      dataIndex: 'taskId',
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('deliveryDate'),
      dataIndex: 'taskStartTime',
      sorter: true,
      showSorterTooltip: false,
      render: (_, data) => {
        return (
          <div>
            {data?.courierTask.length > 0 &&
              data?.courierTask[0]?.slot?.slotDate[0]?.date}
          </div>
        );
      },
    },
    {
      title: t('slotTime'),
      dataIndex: 'slot',
      sorter: true,
      showSorterTooltip: false,
      render: (_, data) => {
        return (
          <div>
            {data?.courierTask.length > 0 &&
              data?.courierTask[0]?.slot?.startTime +
                '-' +
                data?.courierTask[0]?.slot?.endTime}
          </div>
        );
      },
    },
    {
      title: t('routingSolveStatus'),
      dataIndex: 'status',
      sorter: true,
      showSorterTooltip: false,
      render: (dataItem) => (dataItem ? `${t(dataItem)}` : '--'),
    },
    {
      title: `${t('storeCode')}/ ${t('storeName')}`,
      dataIndex: 'taskStartTime',
      showSorterTooltip: false,
      render: (_, data) => {
        return (
          <div>
            {data?.courierTask.length > 0 &&
              data?.courierTask[0]?.slot?.zone?.store?.code +
                '/' +
                data?.courierTask[0]?.slot?.zone?.store?.name}
          </div>
        );
      },
    },
    {
      title: t('zone'),
      dataIndex: 'zone',
      showSorterTooltip: false,
      render: (_, data) => {
        return (
          <div>
            {data?.courierTask.length > 0 &&
              data?.courierTask[0]?.slot?.zone?.name}
          </div>
        );
      },
    },
    {
      title: t('routingStartTime'),
      dataIndex: 'routeStartTime',
      sorter: true,
      showSorterTooltip: false,
      render: (dataItem) => {
        if (dataItem) {
          const utcDate = moment.utc(dataItem).add(3, 'hours');
          const formattedDate = utcDate.format('DD-MM-YYYY / HH:mm');
          return formattedDate;
        } else {
          return '--';
        }
      },
    },
    {
      title: t('routingDoneTime'),
      dataIndex: 'routeEndTime',
      sorter: true,
      showSorterTooltip: false,
      render: (dataItem) => {
        if (dataItem) {
          const utcDate = moment.utc(dataItem).add(3, 'hours');
          const formattedDate = utcDate.format('DD-MM-YYYY / HH:mm');

          return formattedDate;
        } else {
          return '--';
        }
      },
    },
    {
      title: `${t('time')} (${t('min')})`,
      dataIndex: 'time',
      sorter: true,
      showSorterTooltip: false,
      render: (_, data) => {
        const routeStartTime = moment(data.routeStartTime);
        const routeEndTime = moment(data.routeEndTime);

        const timeDifferenceMinutes = routeEndTime.diff(
          routeStartTime,
          'minutes',
        );

        const displayValue = isNaN(timeDifferenceMinutes)
          ? '--'
          : timeDifferenceMinutes;

        return <div>{displayValue}</div>;
      },
    },
    {
      title: t('plannedRouteEndTime'),
      dataIndex: 'time',
      sorter: true,
      showSorterTooltip: false,
      render: (_, data) => {
        const routeStartTime = moment.utc(data.routeStartTime).add(3, 'hours');
        const estimatedTravelDuration =
          data.routeMetric?.estimatedTravelDuration || 0;

        const timeDifferenceMinutes = estimatedTravelDuration || 0;

        const routeEndTime = routeStartTime
          .clone()
          .add(timeDifferenceMinutes, 'minutes');
        const formattedEndTime = routeEndTime.isValid()
          ? routeEndTime.format('DD-MM-YYYY / HH:mm')
          : '--';

        return <div>{formattedEndTime}</div>;
      },
    },
    {
      title: t('courierCount'),
      dataIndex: 'routeCount',
      sorter: true,
      showSorterTooltip: false,
      render: (item, data) => {
        return <div>{data?.routeMetric?.routeCount}</div>;
      },
    },
    {
      title: `${t('distance')} (${t('km')})`,
      dataIndex: 'estimatedRouteLength',
      sorter: true,
      showSorterTooltip: false,
      render: (item, data) => {
        const meters = data?.routeMetric?.estimatedRouteLength || 0;
        const kilometers = (meters / 1000).toFixed(3);

        return <div>{kilometers}</div>;
      },
    },
    {
      title: t('completedAndTotalOrders'),
      dataIndex: 'completedOrderCount',
      sorter: true,
      width: '200px',
      showSorterTooltip: false,
      render: (item, data) => {
        const completedOrderCount = data?.completedOrderCount || 0;
        const totalOrderCount = data?.orderIncludedSize || 0;

        return <div>{`${completedOrderCount}/${totalOrderCount}`}</div>;
      },
    },
    {
      width: 50,
      fixed: 'right',
      render: (dataItem) =>
        isCrudEnabled && (
          <Button
            shape="circle"
            icon={<SendOutlined />}
            onClick={() => {
              setSelectedRoutingDetailData(dataItem);
              history.push(
                `${PARENT_ROUTES.ROUTING_MANAGEMENT}${PAGE_URLS.ROUTING_DETAIL}`,
              );
            }}
          ></Button>
        ),
    },
  ];

  const actions = [
    {
      type: 'primary',
      title: t('add'),
      icon: <PlusOutlined />,
      onClick: () =>
        history.push(
          `${PARENT_ROUTES.ROUTING_MANAGEMENT}${PAGE_URLS.ROUTING_PLANNING}`,
        ),
      disabled: false,
    },
    {
      isCrudEnabled: true,
      type: '',
      title: t('download'),
      onClick: () => {
        fetchCSVStoresData();
      },
      icon: <DownloadOutlined />,
      disabled: exportButtonLoading, // exportButtonLoading
      loading: exportButtonLoading,
    },
    {
      type: 'primary',
      title: t('slotManagement.delete'),
      icon: <DeleteOutlined />,
      onClick: () => removeRoutingViewListModal(),
      danger: true,
      disabled: !routeTaskIdList.length,
    },
    {
      type: 'primary',
      title: t('reload'),
      icon: <RedoOutlined />,
      onClick: () => {
        fetchData(true);
      },
      ghost: true,
    },
  ];

  const removeRoutingViewListModal = () => {
    confirm({
      title: t('routesSelectedWillDeleted'),
      icon: <ExclamationCircleOutlined />,
      content: t('areYouSureRoutesSelectedWillDeleted'),
      okText: t('yes'),
      okType: 'danger',
      cancelText: t('no'),
      onOk: () => {
        removeRoutingViewListData({ routeTaskidList: routeTaskIdList }).then(
          () => {
            fetchData();
            notificationSave(STATUS.SUCCESS);
            setRouteTaskIdList([]);
          },
        );
      },
      onCancel() {},
    });
  };

  const notificationSave = (type) => {
    notification[type]({
      message: t('deleted'),
      description: t('deletedRecord'),
    });
  };

  const fetchCSVStoresData = async () => {
    setExportButtonLoading(true);
    const routingViewLists = await getRoutingViewList('?&page=0&size=2000');
    const routingViewListsData =
      routingViewLists?._embedded?.routeTaskResponseDtoList;

    if (routingViewListsData && routingViewListsData.length > 0) {
      const csvData = [
        CSV_ROUTING_EXPORT_COLUMNS.map((item) => t(item)),
        ...routingViewListsData.map((item) => [
          item.taskId,
          item.taskStartTime,
          item.slot,
          item.status,
          item.taskStartTime,
          item.zone,
          item.taskStartTime,
          item.taskEndTime,
          item.time,
          item.routeCount,
          item.estimatedRouteLength,
        ]),
      ];

      downloadCSV(csvData);
      setExportButtonLoading(false);
    }
  };

  const downloadCSV = (csvData) => {
    const csvContent =
      'data:text/csv;charset=utf-8,' +
      csvData.map((row) => row.join(',')).join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'Zones.csv');
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderActionButtons = () => {
    return actions.map((item, idx) => {
      return (
        <Tooltip title={item.title} key={idx}>
          {item.customContent ?? (
            <Button
              ghost={item?.ghost}
              type={item?.type}
              shape="round"
              icon={item?.icon}
              onClick={item.onClick}
              disabled={item.disabled}
              style={{ marginBottom: '10px', marginRight: '3px' }}
              danger={item?.danger}
            />
          )}
        </Tooltip>
      );
    });
  };

  const renderSelectedFilters = () => {
    return Object.keys(filters).map((item, idx) => {
      if (filters[item]) {
        return (
          <Tag
            key={idx}
            closable
            color="geekblue"
            onClose={(e) => {
              e.preventDefault();
              removeItemFromFilters(item);
            }}
          >
            {`${t(item)} : ${item == 'taskStartTime' || item == 'taskEndTime' || item == 'startTime' || item == 'endTime' ? filters[item] : item == 'zone' || item == 'store' || item == 'status' ? 'Birden fazla...' : t(`${filters[item]}`)}`}
          </Tag>
        );
      }
    });
  };

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      setRouteTaskIdList(selectedRows.map((item) => item.id));
    },
    getCheckboxProps: (record) => ({
      disabled: record.status === SOLVER_STATUS.CALCULATING,
      name: record.taskId,
    }),
  };

  return (
    <div style={{ position: 'relative' }}>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
        }}
      >
        <Col>{renderActionButtons()}</Col>
        <Col>
          <Button
            shape="round"
            icon={<FilterOutlined />}
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
          >
            {t('filters')}
          </Button>
        </Col>
      </Row>
      <Row style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {renderSelectedFilters()}
      </Row>
      <Row style={{ marginTop: '10px' }}>
        <ConfigProvider locale={trTR}>
          <Table
            style={{ width: '100%' }}
            size="small"
            columns={columns}
            dataSource={rows}
            pagination={tableParams.pagination}
            loading={loading}
            onChange={handleTableChange}
            rowKey={(record) => record.id}
            scroll={{
              y: '70vh',
              x: '2000px',
            }}
            rowSelection={{
              type: 'checkbox',
              ...rowSelection,
            }}
          />
        </ConfigProvider>
      </Row>
      <Suspense fallback={<></>}>
        {isFiltersOpen && (
          <RoutingViewListFilter
            setIsFiltersOpen={setIsFiltersOpen}
            setFilters={setFilters}
            form={form}
          />
        )}
      </Suspense>
    </div>
  );
};

export default RoutingViewList;
